import { createContext, useCallback, useContext, type ReactNode } from 'react';

import type { ApprovalDecision } from '../../../../lib/bizy/types/chat';
import type { BizyUiBlockAction } from '../../../../lib/bizy/types/ui-blocks';

/**
 * The UI-block action dispatcher. The renderer never performs the
 * side-effects itself — it hands a typed action to the host, which maps
 * each `type` to the right behavior (composer insert, give-box open,
 * navigate, send chat message, …).
 *
 * In bizy-kit this is just the seam: a context + a no-op default. The
 * concrete implementation is supplied by each app:
 *   - Kaleidoscope wires give-box / composer / chat (PR5).
 *   - Bizy Desktop wires `openExternal` / filtered actions.
 */
export type BizyUiBlockActionDispatcher = (action: BizyUiBlockAction) => void;

const BizyUiBlockActionDispatcherContext = createContext<BizyUiBlockActionDispatcher | null>(null);

export function BizyUiBlockActionProvider({ dispatch, children }: { dispatch?: BizyUiBlockActionDispatcher; children: ReactNode }) {
	return <BizyUiBlockActionDispatcherContext.Provider value={dispatch ?? null}>{children}</BizyUiBlockActionDispatcherContext.Provider>;
}

/**
 * Reads the current dispatcher from context. Returns a no-op function
 * when no provider exists so cards render safely outside the chat view.
 */
export function useBizyUiBlockActionDispatcher(): BizyUiBlockActionDispatcher {
	const dispatcher = useContext(BizyUiBlockActionDispatcherContext);
	return useCallback(
		(action: BizyUiBlockAction) => {
			dispatcher?.(action);
		},
		[dispatcher]
	);
}

/**
 * Give-box hand-off payload for the legacy `draft_card`. Web builds its
 * give-box editor content from these structured fields and opens the
 * modal; Desktop opens a Bonusly link.
 */
export interface BizyGiveBoxDraft {
	recipients?: string[];
	amount?: number;
	message?: string;
	hashtags?: string[];
}

/**
 * Host-resolved post-wrap (gift wrap) info for the recognition draft card's
 * "Wrap this post" toggle. `onBuyWrap` opens the host's Rewards flow when the
 * user owns none.
 */
export interface BizyWrapInfo {
	/** Company has post wraps enabled (collectibles + an active catalog item). */
	enabled: boolean;
	/** The user's active (first usable) wrap, or null when they own none. */
	activeWrap: { previewUrl?: string; remainingUses: number; totalUses: number } | null;
	/** Open the host's Rewards flow to buy a wrap (offered when enabled + none owned). */
	onBuyWrap?: () => void;
}

/**
 * Document-level config, callbacks + flags shared by the node/card
 * components. All optional with no-op / inert defaults so the
 * DocumentRenderer works with nothing wired (read-only previews, tests).
 */
export interface BizyChatActions {
	/** Body font size forwarded to markdown + follow-up nodes. */
	fontSize?: 'sm' | 'md';
	/** Whether the streaming turn is complete (clears in-progress markdown styling). */
	isComplete: boolean;
	/** Tool progress labels shown on the active (last) markdown node while tools run. */
	toolProgress?: string[];
	/** Rotating thinking phrase shown on the active markdown node. */
	thinkingPhrase?: string;
	/** Called when content updates (auto-scroll hook). */
	onContentUpdate?: () => void;
	/** Disables customer-affecting card actions while keeping links/text selectable. */
	isReadOnly: boolean;
	/** Disables follow-up rows + drill-in while a previous turn is processing. */
	followUpsDisabled: boolean;
	/** Stable key for the message — card components record per-card choices against it. */
	messageKey?: string;
	/** Post-click state keyed by interactive node position (e.g. "Sent ✓" after reload). */
	nodeChoices?: Record<number, ApprovalDecision>;
	/** Per-card confirm — sends the card's confirm message verbatim. */
	onConfirm?: (cardIndex: number, confirmMessage: string) => void;
	/** Records a per-card choice so post-click state survives reloads. */
	onChoiceUpdate?: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
	/** Send a follow-up suggestion verbatim as the next message. */
	onSelectFollowUp?: (suggestion: string) => void;
	/** Drill into an evidence element — sends a renderer-built scoped follow-up. */
	onDrill?: (message: string) => void;
	/** Suggested in-app page link clicked from a markdown node (analytics + navigate). */
	onSuggestedPageClick?: (path: string) => void;
	/** Open the host give-box prefilled from a legacy draft_card. */
	onOpenGiveBox?: (draft: BizyGiveBoxDraft) => void;
	/** Post-wrap toggle data for the recognition draft card (host-resolved). */
	wrap?: BizyWrapInfo;
}

const defaultActions: BizyChatActions = {
	isComplete: true,
	isReadOnly: false,
	followUpsDisabled: false,
};

const BizyChatActionsContext = createContext<BizyChatActions>(defaultActions);

export function BizyChatActionsProvider({ value, children }: { value: BizyChatActions; children: ReactNode }) {
	return <BizyChatActionsContext.Provider value={value}>{children}</BizyChatActionsContext.Provider>;
}

export function useBizyChatActions(): BizyChatActions {
	return useContext(BizyChatActionsContext);
}
